import { api } from "@shared-server/api";
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@shared-components/shadcn/chart";
import {
  CartesianGrid,
  XAxis,
  YAxis,
  BarChart,
  Tooltip,
  Legend,
  Bar,
} from "recharts";
import React from "react";
import { COLORS } from "./metric-utils";
import type { GranularityType } from "@shared-server/api/routers/queries/metrics/utils";
import { useDepartmentStackContext } from "@shared-components/department-stack-provider";

type ActiveUsersPerToolProps = {
  startDate: Date;
  endDate: Date;
  granularity: GranularityType;
};

const chartConfig: ChartConfig = {
  count: {
    label: "Active users per tool",
  },
};

export const ActiveUsersPerTool = (props: ActiveUsersPerToolProps) => {
  const { parentDepartmentId } = useDepartmentStackContext();
  const { data } = api.coreMetrics.activeUsersPerTool.useQuery({
    dateStart: props.startDate,
    dateEnd: props.endDate,
    granularity: props.granularity,
    initialDepartmentId: parentDepartmentId,
  });

  const chartData = Array.isArray(data?.overall) ? data.overall : [];
  const chartDataByDepartment = data?.byDepartment || [];

  const toolNames =
    chartData.length > 0 && chartData[0]
      ? Object.keys(chartData[0]).filter((key) => key !== "date")
      : [];

  return (
    <>
      <h2 className="mb-4">{"Active users per tool"}</h2>
      {chartData.length !== 0 && (
        <div className="mt-8 flex h-[300px] w-full items-start">
          <div className="flex h-full flex-1 flex-col">
            <ChartContainer
              config={chartConfig}
              className="aspect-auto h-full flex-1"
            >
              <BarChart
                data={chartData}
                margin={{ top: 10, right: 30, left: 20, bottom: 10 }}
                height={260}
              >
                <CartesianGrid vertical={false} />
                <XAxis
                  dataKey="date"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  fill="#006e96"
                  minTickGap={32}
                />
                <YAxis yAxisId="count" interval={1} orientation="left" />
                <ChartTooltip
                  content={
                    <ChartTooltipContent
                      className="w-[150px]"
                      nameKey="date"
                      labelFormatter={(value: string) => value}
                    />
                  }
                />
                {toolNames.map((tool, idx) => (
                  <Bar
                    stackId={"tool"}
                    key={tool}
                    yAxisId="count"
                    type="monotone"
                    dataKey={tool}
                    name={tool}
                    fill={COLORS[idx % COLORS.length]}
                  />
                ))}
              </BarChart>
            </ChartContainer>
            <div className="mt-2 flex h-[32px] flex-wrap items-center justify-center">
              {toolNames.map((tool, idx) => (
                <span key={tool} className="mr-4 flex items-center text-sm">
                  <span
                    className="mr-1 inline-block h-3 w-3 rounded-full"
                    style={{ background: COLORS[idx % COLORS.length] }}
                  />
                  {tool}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
      {chartData.length === 0 && (
        <div className="text-gray-500">
          {"No data available for selected range."}
        </div>
      )}
      <hr />
      <div className="flex row gap-3 items-center mt-3">
        <h2 className="mb-4">{"Departments categorization"}</h2>
      </div>
      <ChartContainer
        config={chartConfig}
        className="min-h-[150px] h-[775px] w-full"
      >
        <BarChart
          accessibilityLayer
          layout="vertical"
          data={chartDataByDepartment}
          margin={{ left: 35, top: 15, right: 15 }}
        >
          <CartesianGrid />
          <XAxis
            tickLine={false}
            axisLine={false}
            fill="#006e96"
            xAxisId="count"
            interval={0}
            type="number"
          />
          <YAxis
            interval={0}
            orientation="left"
            dataKey="departmentName"
            type="category"
          />
          <ChartTooltip
            wrapperStyle={{ zIndex: 1 }}
            content={
              <ChartTooltipContent
                className="max-w-[350px]"
                itemsContainerClassName="grid grid-cols-3 gap-2"
              />
            }
          />
          <Tooltip />

          <Legend />
          <Bar
            key="tool"
            xAxisId="count"
            dataKey="toolUsage"
            name="count"
            fill={COLORS[0]}
          />
        </BarChart>
      </ChartContainer>
    </>
  );
};
